import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ReactPlayer from "react-player";
import { Button, Dropdown, Slider, Space, message } from "antd";
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  SoundOutlined,
  ReloadOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import io from 'socket.io-client';
import '../../styles/MyVideoPlayer.css';

const rates = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const MyVideoPlayer = React.memo(function MyVideoPlayer({ url, style, lessonId, authToken, onTrackingUpdate, initialTracking, onCompletionChange, disableTracking = false }) {
  const playerRef = useRef();
  const containerRef = useRef();
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideTimer = useRef();
  const [isBuffering, setIsBuffering] = useState(false);
  const [showCenterIcon, setShowCenterIcon] = useState(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  
  // Watch time tracking (now handled by Web Worker)
  const [totalWatchTime, setTotalWatchTime] = useState(initialTracking?.totalWatchedTime || 0);
  const trackingWorker = useRef(null);
  const lastProgressUpdate = useRef(0);
  
  // Lesson tracking state
  const [lessonTracking, setLessonTracking] = useState({
    currentTime: initialTracking?.currentTime || 0,
    totalWatchedTime: initialTracking?.totalWatchedTime || 0,
    videoDuration: initialTracking?.videoDuration || 0,
    completed: initialTracking?.completed || false,
    lastWatchedAt: initialTracking?.lastWatchedAt || null
  });
  
  // WebSocket connection
  const socketRef = useRef(null);
  const trackingIntervalRef = useRef(null);
  const [isTrackingConnected, setIsTrackingConnected] = useState(false);
  
  // Completion percentage threshold (80%)
  const COMPLETION_THRESHOLD = 0.8;
  
  // Send tracking update to server (non-blocking)
  const sendTrackingUpdate = useCallback((currentTime, totalWatchedTime, videoDuration) => {
    if (socketRef.current && socketRef.current.connected) {
      const updateData = {
        lessonId,
        currentTime: Math.floor(currentTime),
        totalWatchedTime: Math.floor(totalWatchedTime),
        videoDuration: Math.floor(videoDuration)
      };
      
      // Use setTimeout to make it non-blocking
      setTimeout(() => {
        socketRef.current.emit('lesson_tracking_update', updateData);
      }, 0);
    }
  }, [lessonId]);
  
  // Stable callback references
  const onTrackingUpdateRef = useRef(onTrackingUpdate);
  const onCompletionChangeRef = useRef(onCompletionChange);
  const sendTrackingUpdateRef = useRef(sendTrackingUpdate);
  
  // Update refs when callbacks change
  useEffect(() => {
    onTrackingUpdateRef.current = onTrackingUpdate;
  }, [onTrackingUpdate]);
  
  useEffect(() => {
    onCompletionChangeRef.current = onCompletionChange;
  }, [onCompletionChange]);
  
  useEffect(() => {
    sendTrackingUpdateRef.current = sendTrackingUpdate;
  }, [sendTrackingUpdate]);

  // Initialize Web Worker for background tracking
  useEffect(() => {
    // Skip worker initialization if tracking is disabled
    if (disableTracking) {
      return;
    }
    
    // Create Web Worker
    trackingWorker.current = new Worker(new URL('../../workers/trackingWorker.js', import.meta.url));
    
    // Handle messages from Web Worker
    trackingWorker.current.onmessage = (e) => {
      const { type, data } = e.data;
      
      switch (type) {
        case 'initialized':
          console.log('Tracking worker initialized');
          break;
          
        case 'watchTimeUpdate':
          // Update watch time from worker
          const { totalWatchTime, currentTime, videoDuration, completed } = data;
          
          setTotalWatchTime(prev => {
            if (prev !== totalWatchTime) {
              return totalWatchTime;
            }
            return prev;
          });
          
          setLessonTracking(prev => {
            // Only update if values actually changed
            if (prev.totalWatchedTime !== totalWatchTime || 
                Math.abs(prev.currentTime - currentTime) > 1 ||
                prev.completed !== completed) {
              
              // Trigger completion callback only when status changes
              if (completed && !prev.completed && onCompletionChangeRef.current) {
                setTimeout(() => onCompletionChangeRef.current(true), 0);
              }
              
              return {
                ...prev,
                totalWatchedTime: totalWatchTime,
                currentTime: currentTime,
                videoDuration: videoDuration,
                completed: completed
              };
            }
            return prev;
          });
          break;
          
        case 'serverUpdate':
          // Handle server update from worker (background process)
          const serverData = data;
          
          // Only send to server, don't update local state (already updated by watchTimeUpdate)
          // This prevents duplicate state updates that cause flickering
          setTimeout(() => {
            sendTrackingUpdateRef.current(serverData.currentTime, serverData.totalWatchTime, serverData.videoDuration);
          }, 0);
          break;
          
        default:
          console.warn('Unknown worker message type:', type);
      }
    };
    
    trackingWorker.current.onerror = (error) => {
      console.error('Tracking worker error:', error?.message || error || 'Unknown worker error');
    };
    
    // Cleanup on unmount
    return () => {
      if (trackingWorker.current) {
        trackingWorker.current.terminate();
        trackingWorker.current = null;
      }
    };
  }, [disableTracking]); // Re-run if disableTracking changes

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };
  
  // WebSocket connection management
  const connectToTracking = useCallback(() => {
    if (!lessonId || !authToken || socketRef.current || disableTracking) return;
    
    try {
      const socket = io(`${process.env.REACT_APP_API_URL || 'http://localhost:3333'}/learner/lesson-tracking`, {
        auth: {
          token: authToken
        },
        transports: ['websocket', 'polling']
      });
      
      socket.on('connect', () => {
        console.log('Connected to lesson tracking');
        setIsTrackingConnected(true);
        socket.emit('join_lesson', { lessonId });
      });
      
      socket.on('lesson_joined', (data) => {
        console.log('Joined lesson:', data);
        if (data.tracking) {
          setLessonTracking(prev => ({ ...prev, ...data.tracking }));
          setTotalWatchTime(data.tracking.totalWatchedTime || 0);
          setHasServerData(true);
          
          // Update worker with fresh server data if already initialized
          if (trackingWorker.current && workerInitialized && typeof data.tracking.totalWatchedTime === 'number') {
            trackingWorker.current.postMessage({
              type: 'updateTotalWatchedTime',
              data: { totalWatchedTime: data.tracking.totalWatchedTime }
            });
          }
        } else {
          // No existing tracking data, set defaults and mark as having server data
          setHasServerData(true);
        }
      });
      
      socket.on('tracking_updated', (data) => {
        console.log('Tracking updated:', data);
        setLessonTracking(prev => ({ ...prev, ...data }));
        
        // Update worker if server sends updated totalWatchedTime
        if (trackingWorker.current && typeof data.totalWatchedTime === 'number') {
          trackingWorker.current.postMessage({
            type: 'updateTotalWatchedTime',
            data: { totalWatchedTime: data.totalWatchedTime }
          });
        }
        
        if (onTrackingUpdate) {
          onTrackingUpdate(data);
        }
      });
      
      socket.on('connect_error', (error) => {
        console.error('Connection error:', error?.message || 'Unknown connection error');
        setIsTrackingConnected(false);
      });
      
      socket.on('tracking_error', (error) => {
        console.error('Tracking error:', error?.message || 'Unknown tracking error');
        message.error('Failed to update lesson progress');
      });
      
      socketRef.current = socket;
    } catch (error) {
      console.error('Failed to connect to tracking:', error);
    }
  }, [lessonId, authToken, onTrackingUpdate, disableTracking]);
  
  const disconnectFromTracking = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.emit('leave_lesson', { lessonId });
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsTrackingConnected(false);
    }
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
  }, [lessonId]);
  
  // Completion checking now handled directly in server update interval

  // Watch time tracking functions removed - now handled directly in intervals

  const handlePlayStateChange = (isPlaying, currentTime) => {
    // Skip tracking if disabled
    if (disableTracking) return;
    
    const position = currentTime || (playerRef.current ? playerRef.current.getCurrentTime() : 0);
    
    console.log('Play state changed:', { isPlaying, position, workerExists: !!trackingWorker.current });
    
    if (trackingWorker.current) {
      if (isPlaying) {
        // Starting to play - notify worker to start tracking
        console.log('Sending startTracking to worker');
        trackingWorker.current.postMessage({
          type: 'startTracking',
          data: { currentTime: position }
        });
      } else {
        // Stopped playing - notify worker to stop tracking
        console.log('Sending stopTracking to worker');
        trackingWorker.current.postMessage({
          type: 'stopTracking'
        });
      }
    }
  };

  const handleSeek = (newPosition) => {
    // Skip tracking if disabled
    if (disableTracking) return;
    
    // Notify worker about position change
    if (trackingWorker.current) {
      trackingWorker.current.postMessage({
        type: 'updatePosition',
        data: { currentTime: newPosition }
      });
    }
  };

  const showControlsBar = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  const hideControlsBar = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    setShowControls(false);
  }, []);

  const handleFullscreen = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      if (el.requestFullscreen) el.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  // Connection management effects
  useEffect(() => {
    if (lessonId && authToken && !disableTracking) {
      connectToTracking();
    }
    return () => {
      disconnectFromTracking();
    };
  }, [lessonId, authToken, connectToTracking, disconnectFromTracking, disableTracking]);
  
  // Initialize Web Worker with video data when duration is available (only once per lesson)
  const [workerInitialized, setWorkerInitialized] = useState(false);
  const [hasServerData, setHasServerData] = useState(false);
  const [initialTrackingSent, setInitialTrackingSent] = useState(false);
  
  // Reset worker initialization flag when lesson changes
  useEffect(() => {
    if (!disableTracking) {
      setWorkerInitialized(false);
      setHasServerData(false);
      setInitialTrackingSent(false);
    }
  }, [lessonId, disableTracking]);
  
  useEffect(() => {
    if (disableTracking) return;
    
    if (trackingWorker.current && duration > 0 && !workerInitialized && hasServerData) {
      trackingWorker.current.postMessage({
        type: 'initialize',
        data: {
          initialTracking: lessonTracking,
          duration,
          playbackRate
        }
      });
      setWorkerInitialized(true);
    }
  }, [duration, playbackRate, workerInitialized, hasServerData, lessonTracking, disableTracking]);
  
  // Send immediate tracking update when video player is ready (on lesson access)
  useEffect(() => {
    if (disableTracking) return;
    
    if (workerInitialized && hasServerData && !initialTrackingSent && lessonId) {
      // Send immediate tracking update to server when user accesses the video lesson
      const immediateUpdate = {
        lessonId,
        currentTime: Math.floor(lessonTracking.currentTime || 0),
        totalWatchedTime: Math.floor(lessonTracking.totalWatchedTime || 0),
        videoDuration: Math.floor(duration || 0)
      };
      
      console.log('Sending immediate tracking update on lesson access:', immediateUpdate);
      
      // Send to server immediately
      if (socketRef.current && socketRef.current.connected) {
        socketRef.current.emit('lesson_tracking_update', immediateUpdate);
      }
      
      // Also trigger the tracking update callback
      if (onTrackingUpdateRef.current) {
        onTrackingUpdateRef.current({
          currentTime: immediateUpdate.currentTime,
          totalWatchedTime: immediateUpdate.totalWatchedTime,
          videoDuration: immediateUpdate.videoDuration,
          completed: lessonTracking.completed || false,
          lastWatchedAt: new Date().toISOString()
        });
      }
      
      setInitialTrackingSent(true);
    }
  }, [workerInitialized, hasServerData, initialTrackingSent, lessonId, lessonTracking, duration, disableTracking]);
  
  // Update worker when playback rate changes
  useEffect(() => {
    if (disableTracking) return;
    
    if (trackingWorker.current && duration > 0) {
      trackingWorker.current.postMessage({
        type: 'updatePlaybackRate',
        data: { rate: playbackRate }
      });
    }
  }, [playbackRate, duration, disableTracking]);
  
  // Update worker when duration changes
  useEffect(() => {
    if (disableTracking) return;
    
    if (trackingWorker.current && duration > 0) {
      trackingWorker.current.postMessage({
        type: 'updateDuration',
        data: { duration }
      });
    }
  }, [duration, disableTracking]);
  
  // Web Worker handles all tracking automatically - no intervals needed on main thread
  
  // Resume video from last position (only on initial load)
  const [hasResumed, setHasResumed] = useState(false);
  useEffect(() => {
    if (playerRef.current && lessonTracking.currentTime > 0 && duration > 0 && !hasResumed) {
      const resumeTime = Math.min(lessonTracking.currentTime, duration - 1);
      playerRef.current.seekTo(resumeTime, 'seconds');
      setPlayed(resumeTime / duration);
      setHasResumed(true);
    }
  }, [duration, lessonTracking.currentTime, hasResumed]);
  
  React.useEffect(() => {
    const onFullScreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", onFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFullScreenChange);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const handleSeekSlider = useCallback((value) => {
    const newPosition = value * duration;
    handleSeek(newPosition);
    setPlayed(value);
    if (playerRef.current) {
      playerRef.current.seekTo(value, "fraction");
    }
  }, [duration]);

  const handleRate = useCallback((rate) => setPlaybackRate(rate), []);

  const rewind = useCallback(() => {
    if (playerRef.current) {
      const current = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.max(current - 10, 0), "seconds");
    }
  }, []);

  const forward = useCallback(() => {
    if (playerRef.current) {
      const current = playerRef.current.getCurrentTime();
      playerRef.current.seekTo(Math.min(current + 10, duration), "seconds");
    }
  }, [duration]);

  const rateMenu = useMemo(() => (
    <div
      style={{
        background: "#222",
        borderRadius: 8,
        padding: 6,
        color: "#fff",
        boxShadow: "0 2px 8px #2227",
      }}
    >
      {rates.map((rate) => (
        <div
          key={rate}
          style={{
            padding: "4px 16px",
            color: rate === playbackRate ? "#a259f7" : "#fff",
            cursor: "pointer",
            fontWeight: rate === playbackRate ? 700 : 400,
          }}
          onClick={() => handleRate(rate)}
        >
          {rate}x
        </div>
      ))}
    </div>
  ), [playbackRate, handleRate]);

  const handleTogglePlay = useCallback(() => {
    setShowCenterIcon(playing ? 'pause' : 'play');
    setPlaying((p) => !p);
  }, [playing]);

  React.useEffect(() => {
    if (showCenterIcon !== null) {
      const timeout = setTimeout(() => setShowCenterIcon(null), 500);
      return () => clearTimeout(timeout);
    }
  }, [showCenterIcon]);

  React.useEffect(() => {
    if (!showControls && dropdownOpen) setDropdownOpen(false);
  }, [showControls, dropdownOpen]);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: isFullscreen ? '100vh' : 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#18181b',
        borderRadius: isFullscreen ? 0 : 12,
        overflow: 'hidden',
        ...style,
        transition: 'all 0.2s',
        zIndex: 1000,
      }}
     
      tabIndex={0}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          aspectRatio: isFullscreen ? undefined : '16/9',
          background: '#000',
          overflow: 'hidden',
          borderRadius: isFullscreen ? 0 : 12,
          transition: 'all 0.2s',
          flexShrink: 0,
          cursor: 'pointer',
        }}
        onClick={handleTogglePlay}
      >
        <div style={{
          position: 'absolute', left: 0, top: 0, width: '100%', height: 0,
          background: 'rgba(24,24,27,0.7)', pointerEvents: 'none', zIndex: 2,
        }} />
        <div style={{
          position: 'absolute', left: 0, bottom: 0, width: '100%', height: 0,
          background: 'rgba(24,24,27,0.7)', pointerEvents: 'none', zIndex: 2,
        }} />
        <div style={{
          position: 'absolute', left: 0, top: 0, width: 0, height: '100%',
          background: 'rgba(24,24,27,0.7)', pointerEvents: 'none', zIndex: 2,
        }} />
        <div style={{
          position: 'absolute', right: 0, top: 0, width: 0, height: '100%',
          background: 'rgba(24,24,27,0.7)', pointerEvents: 'none', zIndex: 2,
        }} />
        <ReactPlayer
          ref={playerRef}
          url={url}
          playing={playing}
          controls={false}
          width="100%"
          height="100%"
          volume={volume}
          playbackRate={playbackRate}
          style={{
            background: "#000",
            objectFit: "cover",
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
          onProgress={useCallback((state) => {
            // Throttle progress updates to reduce re-renders
            const now = Date.now();
            if (!lastProgressUpdate.current || now - lastProgressUpdate.current > 100) {
              setPlayed(state.played);
              lastProgressUpdate.current = now;
            }
            
            // Send position updates to Web Worker during playback
            if (trackingWorker.current && playing) {
              trackingWorker.current.postMessage({
                type: 'updatePosition',
                data: { currentTime: state.playedSeconds }
              });
            }
          }, [playing])}
          onDuration={useCallback((duration) => {
            setDuration(duration);
            // Initialize tracking with existing data or reset
            if (!lessonTracking.videoDuration) {
              setLessonTracking(prev => ({ ...prev, videoDuration: duration }));
            }
            // Only reset if this is a completely different video (significant duration change)
            // Don't reset for the same video or minor duration differences
            if (lessonTracking.videoDuration && Math.abs(lessonTracking.videoDuration - duration) > 10) {
              setTotalWatchTime(0);
              // Reset tracking in Web Worker
              if (trackingWorker.current) {
                trackingWorker.current.postMessage({
                  type: 'init',
                  data: { duration, playbackRate }
                });
              }
            }
          }, [lessonTracking.videoDuration, playbackRate])}
          onReady={useCallback(() => {
            // ReactPlayer ready
          }, [])}
          onStart={useCallback(() => {
            // ReactPlayer started
            const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
            handlePlayStateChange(true, currentTime);
          }, [])}
          onBuffer={useCallback(() => {
            // Buffering started
            const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
            handlePlayStateChange(false, currentTime);
            setIsBuffering(true);
          }, [])}
          onBufferEnd={useCallback(() => {
            // Buffering ended
            const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
            if (playing) {
              handlePlayStateChange(true, currentTime);
            }
            setIsBuffering(false);
          }, [playing])}
          onPlay={useCallback(() => {
            // Play triggered
            setPlaying(true);
            const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
            handlePlayStateChange(true, currentTime);
          }, [])}
          onPause={useCallback(() => {
            // Pause triggered
            setPlaying(false);
            const currentTime = playerRef.current ? playerRef.current.getCurrentTime() : 0;
            handlePlayStateChange(false, currentTime);
          }, [])}

          onError={(error) => {
            console.error('ReactPlayer error:', {
              message: error?.message || 'Unknown player error',
              error: error,
              currentTime: playerRef.current ? playerRef.current.getCurrentTime() : 'N/A',
              duration: duration
            });
          }}
        />
        {showCenterIcon && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 25,
              background: 'rgba(0,0,0,0.25)',
              borderRadius: '50%',
              width: 90,
              height: 90,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {showCenterIcon === 'play' ? (
              <PlayCircleOutlined style={{ fontSize: 72, color: '#fff' }} />
            ) : (
              <PauseCircleOutlined style={{ fontSize: 72, color: '#fff' }} />
            )}
          </div>
        )}
        {isBuffering && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 30,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div className="video-center-spinner" style={{
              width: 60,
              height: 60,
              border: '6px solid #fff3',
              borderTop: '6px solid #a259f7',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }} />
          </div>
        )}
        <div
          style={{
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? "auto" : "none",
            transition: "opacity 0.3s",
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            background: "rgba(24,24,27,0.28)",
            backdropFilter: "blur(1px)",
            borderBottomLeftRadius: isFullscreen ? 0 : 12,
            borderBottomRightRadius: isFullscreen ? 0 : 12,
          }}
          onClick={e => e.stopPropagation()}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "18px 24px 8px 24px",
            }}
          >
            <span style={{ color: "#fff", width: 46, fontSize: 13 }}>{formatTime(played * duration)}</span>
            <Slider
                min={0}
                max={1}
                step={0.01}
                value={played}
                onChange={handleSeekSlider}
                onAfterChange={handleSeekSlider}
                tooltip={{ open: false }}
                style={{
                  flex: 1,
                  margin: "0 10px",
                  height: 4,
                }}
                trackStyle={{ backgroundColor: "#a259f7", height: 4 }}
                railStyle={{ backgroundColor: "#333", height: 4 }}
                handleStyle={{
                  borderColor: "#a259f7",
                  backgroundColor: "#fff",
                }}
              />
            <span style={{ color: "#fff", width: 46, fontSize: 13 }}>{formatTime(duration)}</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 24px 18px 24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ color: "#a259f7", fontSize: 12, fontWeight: 500 }}>
                  Watch Time: {formatTime(totalWatchTime)} / {formatTime(duration)}
                </span>
                <span style={{ color: "#888", fontSize: 12 }}>
                  ({duration > 0 ? Math.round((totalWatchTime / duration) * 100) : 0}%)
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {isTrackingConnected && (
                  <span style={{ color: "#52c41a", fontSize: 10 }}>●</span>
                )}
              </div>
            </div>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={useCallback((e) => { e.stopPropagation(); handleSeekSlider(0); }, [handleSeekSlider])} />
              <Button icon={<FastBackwardOutlined />} onClick={useCallback((e) => { e.stopPropagation(); rewind(); }, [rewind])} />
              <Button
                icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                onClick={useCallback((e) => { e.stopPropagation(); handleTogglePlay(); }, [handleTogglePlay])}
                type="primary"
                style={{ fontSize: 20 }}
                shape="circle"
              />
              <Button icon={<FastForwardOutlined />} onClick={useCallback((e) => { e.stopPropagation(); forward(); }, [forward])} />
              <SoundOutlined style={{ color: "#fff", marginLeft: 16 }} />
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={volume}
                onChange={useCallback((value) => setVolume(value), [])}
                tooltip={{ open: false }}
                style={{ width: 60, margin: "0 8px" }}
                onClick={useCallback((e) => e.stopPropagation(), [])}
              />
            </Space>
            <Space>
              <Dropdown
                overlay={rateMenu}
                trigger={["click"]}
                getPopupContainer={useCallback(() => containerRef.current, [])}
                open={dropdownOpen}
                onOpenChange={useCallback((open) => setDropdownOpen(open), [])}
              >
                <Button style={{ marginLeft: 12 }} onClick={useCallback((e) => e.stopPropagation(), [])}>{playbackRate}x</Button>
              </Dropdown>
              <Button
                icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={useCallback((e) => { e.stopPropagation(); handleFullscreen(); }, [handleFullscreen])}
                style={{ marginLeft: 10 }}
              />
            </Space>
          </div>
        </div>
      </div>
    </div>
  );
});

export default MyVideoPlayer;


